import React, {useCallback, useEffect, useState} from 'react';
import {Col, Container, Row} from 'reactstrap';
import classNames from 'classnames';
import {Location} from '@reach/router';
import queryString from 'query-string';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import useBalanceText from 'utils/useBalanceText';

import PageMeta from 'components/PageMeta';
import Header from 'components/Elements/Header';
import CountdownTimer from 'components/Elements/CountdownTimer';
import OptinModal, {OptinModalTrigger} from 'components/Modals/OptinModal';
import ModalExitLanding from 'components/Modals/ModalExitLanding';
import Footer from 'components/Elements/Footer';
import SectionCL from 'components/ComponentLibrary/SectionCL';
import IconListCL from 'components/ComponentLibrary/IconListCL/IconListCL';

export default function Index(props) {
  return (
    <Location>
      {({location, navigate}) => <MasterClassIndex location={location} navigate={navigate} {...props} />}
    </Location>
  );
}
function MasterClassIndex({
  location = {},
  entry = 'health',
  bucket = 'health',
  noShare = false,
  noFixedVideo = false,
  noExitIntent = true,
  slug = 'masterclass',
  navigate,
  variant = null,
  pageTitle = 'How to Feel Awesome with a Plant-based Diet',
  pageDescription = 'Watch this free mini-masterclass to learn what to eat to feel good again.',
  ogImage = 'https://cdn.foodrevolution.org/ppp/og/ppp-opengraph-health-1200x1200-v20221103.jpg',
  shareUrl = 'https://plantplaybook.foodrevolution.org/masterclass/health/',
  facebookDescription = 'Feeling less healthy than you’d like to be? A plant-based diet has helped millions of people feel good again. Watch this free mini-masterclass for 3 of the top questions — and answers — about a plant-based diet:',
  twitterDescription = 'Feeling less healthy than you’d like to be? A plant-based diet has helped millions of people feel good again. Watch this mini-masterclass, How a Plant-Based Diet Can Make You Feel Better — airing now for free.',
  twitterImg = 'https://cdn.foodrevolution.org/ppp/og/ppp-opengraph-health-1200x630-20221103.jpg',
  pinterestDescription = 'Feeling less healthy than you’d like to be? A plant-based diet has helped millions of people feel good again. Watch this mini-masterclass, How a Plant-Based Diet Can Make You Feel Better — airing now for free.',
  pinterestImage = 'https://cdn.foodrevolution.org/ppp/og/ppp-opengraph-health-1000x1500-20221103.jpg',
  ...props
}) {
  const qs = typeof window !== 'undefined' ? queryString.parse(window.location.search) : {};
  const navigateWatch = useCallback(() => navigate('/masterclass/watch/'), []);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const pathName = window.location.pathname;
      const pathRegex = new RegExp(`\/${slug}\/`, 'g');
      if (!pathName.match(pathRegex)) {
        // @ts-ignore
        navigate(`/${slug}/` + location.search);
      }
    }
  }, []);

  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchEnd: ''
      }
    }
  } = usePhaseState();

  const launchEnd = new Date(defaultSchedule.launchEnd).getTime();
  const pricePointEntry = '27';

  const [sustainabilityHeadlineControl, setsustainabilityHeadlineControl] = useState(
    props.sustainabilityHeadlineControl || 'control'
  );
  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setsustainabilityHeadlineControl('v1');
          break;
      }
    }, 1157);
  }, []);

  const [sustainabilityImageControl, setsustainabilityImageControl] = useState(
    props.sustainabilityImageControl || 'control'
  );
  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setsustainabilityImageControl('v1');
          break;
      }
    }, 1160);
  }, []);

  const curiousImageControl = 'v1';
  const healthImageControl = 'v1';

  const imageSplitTestVariant =
    curiousImageControl === 'v1' || sustainabilityImageControl === 'v1' || healthImageControl === 'v1'
      ? 'v1'
      : 'control';

  function OptinButton({className = null}) {
    return <OptinModalTrigger className={className}>Reserve Your Free Spot Now</OptinModalTrigger>;
  }

  function LandingTimer({className = null, timerClass = null, ...props}) {
    return (
      <div className={classNames(`section-countdown countdown-${props.light ? 'light' : 'dark'}`, className)}>
        <h5 className="timer-heading mt-0 mb-2">{defaultSchedule.launchTitle} ends in:</h5>
        <CountdownTimer time={launchEnd} className={timerClass} />
      </div>
    );
  }

  useBalanceText();
  return (
    <div className="page page-nosocial d-flex flex-column">
      <PageMeta title={pageTitle} description={pageDescription} url={shareUrl} ogImage={ogImage} />
      <div className="page-content">
        <Header
          bgColor="white"
          textColor="black"
          adLockup={variant === 'ad' ? true : false}
          masterclassText={
            <>
              <p className="m-0">
                <b>Masterclass:</b>{' '}
                {
                  {
                    curious: <>Why Go Plant-Based</>,
                    sustainability: <>Your Food and Your Planet</>,
                    health: <>Eating to Feel Awesome</>
                  }[entry]
                }
              </p>
            </>
          }
        />
        <SectionCL
          id="landing-header-poster"
          color="black"
          imgUrl={
            {
              curious:
                curiousImageControl === 'v1'
                  ? 'https://cdn.foodrevolution.org/ppp/images/1284690585-lg.jpg'
                  : 'https://cdn.foodrevolution.org/ppp/images/871227828-lg.jpg',
              sustainability:
                sustainabilityImageControl === 'v1'
                  ? 'https://cdn.foodrevolution.org/ppp/images/1284690585-lg.jpg'
                  : 'https://cdn.foodrevolution.org/ppp/images/1035544898-lg.jpg',
              health:
                healthImageControl === 'v1'
                  ? 'https://cdn.foodrevolution.org/ppp/images/1284690585-lg.jpg'
                  : 'https://cdn.foodrevolution.org/ppp/images/522319544-lg.jpg'
            }[entry]
          }
          className={classNames(
            `landing-header-poster-${entry}`,
            `landing-header-poster-${entry}-${imageSplitTestVariant}`
          )}
          innerWrapClass="my-auto flex-grow-1"
          innerClass="text-white text-center"
          type="triangle"
          angle="none"
        />
        <SectionCL id="landing-header-opt" color="white" angle="both" type="triangle">
          <Container>
            <Row>
              <Col>
                <div className="optin-box background-white rounded box-shadow-black-50 p-4 p-lg-5 text-center">
                  {
                    {
                      curious: (
                        <>
                          <h1 className="text-h3 mt-0">What’s so great about going plant-based?</h1>
                          <h3 className="mt-0">Find out what it can do for you!</h3>
                        </>
                      ),
                      sustainability: (
                        <>
                          {
                            {
                              control: (
                                <h1 className="text-h3 mt-0">
                                  How your food choices can help save the planet (for real)
                                </h1>
                              ),
                              v1: (
                                <h1 className="text-h3 mt-0">
                                  A plant-based is the #1 way you can save water and endangered species, fight climate
                                  change, and contribute to a healthier future. Plus, it can feel really good to know
                                  that you’re part of the solution! Get the truth.
                                </h1>
                              )
                            }[sustainabilityHeadlineControl]
                          }
                        </>
                      ),
                      health: (
                        <h1 className="text-h3 mt-0">
                          Proven ways to tweak a plant-based diet so you feel better than you have in years
                        </h1>
                      )
                    }[entry]
                  }
                  <p className="balance-text mb-4">
                    <i>FREE Mini-masterclass Now Playing for a Limited Time</i>
                  </p>
                  <OptinButton />
                  {variant != 'ad' && (
                    <>{defaultSchedule.phase === 'during' && <LandingTimer className="countdown-small mt-4" />}</>
                  )}
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="landing-discover" color="purple" type="triangle" className="text-center text-white">
          <Container>
            <Row>
              <Col>
                <h3 className="section-only-heading text-400">
                  In This <b>15-Minute Mini-Masterclass</b> You’ll Discover:
                </h3>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="landing-discoverList" color="white" type="triangle" className="text-h5">
          <Container>
            <Row>
              <Col>
                {
                  {
                    curious: (
                      <IconListCL iconColor="purple">
                        <>
                          The results of the world’s most comprehensive long-term health study, tracking the diets and
                          health outcomes of nearly 100,000 people for 40 years. Some of them lived much longer than
                          others. Find out why.{' '}
                          <b className="text-purple">Goodbye confusion, and hello clarity (and radiant health!).</b>
                        </>
                        <>
                          The stunning truth about your food and your planet. You won’t believe how powerful you are and
                          what a difference you can make for water, food security, and our climate.{' '}
                          <b className="text-purple">
                            Goodbye, cynicism, and hello to making a difference on planet Earth!
                          </b>
                        </>
                        <>
                          What the people with the longest (and healthiest) lifespans eat.{' '}
                          <b className="text-purple">Goodbye, fear of aging, and hello, longevity.</b>
                        </>
                        <>
                          The top 3 obstacles to going plant-based and what to do about them. Get the BEST antidote to
                          uninspired meals so you can excite your palate with meals that you’ll always look forward to.{' '}
                          <b className="text-purple">Goodbye, boredom, and hello to loving foods that love you back!</b>
                        </>
                        <>
                          How to dodge the biggest plant-based diet mistake to get the MOST out of your meals.{' '}
                          <b className="text-purple">
                            Goodbye, fear of missing nutrients, and hello to giving your body what it needs to thrive.
                          </b>
                        </>
                      </IconListCL>
                    ),
                    sustainability: (
                      <IconListCL iconColor="purple">
                        <>
                          What uses 83% of the world’s agricultural land, to provide just 18% of the world’s calories.
                        </>
                        <>
                          {
                            {
                              default: <>The #1 way you can take a serious bite out of climate change.</>,
                              ad: <>The #1 way you can take a serious bite out of environmental challenges.</>
                            }[variant]
                          }
                        </>
                        <>
                          {
                            {
                              default: (
                                <>
                                  The stunning truth about world hunger. It turns out that meat production is like a
                                  protein factory in reverse. In fact, we can feed the world abundantly if we stop
                                  cycling so many calories through livestock!
                                </>
                              ),
                              ad: (
                                <>
                                  The stunning truth about how we can feed the world abundantly if we stop cycling so
                                  many calories through livestock!
                                </>
                              )
                            }[variant]
                          }
                        </>
                        <>
                          How the same food choices that are good for your planet, can also give you a longer and
                          healthier life.
                        </>
                        <>
                          The top 3 obstacles to going plant-based and what to do about them. Get the BEST antidote to
                          uninspired meals so you can excite your palate with meals that you’ll always look forward to.
                        </>
                        <>How to dodge the biggest plant-based diet mistake to get the MOST out of your meals.</>
                        <>
                          How to eat for a healthy body and a healthy planet.{' '}
                          <b className="text-purple">You can have it all!</b>
                        </>
                      </IconListCL>
                    ),
                    health: (
                      <IconListCL iconColor="purple">
                        <>
                          What the people with the longest (and healthiest) lifespans eat.{' '}
                          <b className="text-purple">Goodbye, fear of aging, and hello, longevity.</b>
                        </>
                        <>
                          The results of the world’s most comprehensive long-term health study, tracking the diets and
                          health outcomes of nearly 100,000 people for 40 years. Some of them lived much longer than
                          others. Find out why!{' '}
                          <b className="text-purple">Goodbye confusion, and hello clarity (and radiant health!).</b>
                        </>
                        <>
                          The top 3 obstacles to going plant-based and what to do about them. Get the BEST antidote to
                          uninspired meals so you can excite your palate with meals that you’ll always look forward to.{' '}
                          <b className="text-purple">Goodbye, boredom, and hello to loving foods that love you back!</b>
                        </>
                        <>
                          How to dodge the biggest plant-based diet mistake to get the MOST out of your meals.{' '}
                          <b className="text-purple">
                            Goodbye, fear of missing nutrients, and hello to giving your body what it needs to thrive.
                          </b>
                        </>
                        <>
                          The stunning truth about your food and your planet. You won’t believe how powerful you are and
                          what a difference you can make for water, food security, and our climate.{' '}
                          <b className="text-purple">
                            Goodbye, cynicism, and hello to making a difference on planet Earth!
                          </b>
                        </>
                      </IconListCL>
                    )
                  }[entry]
                }
                <h5 className="text-center mt-4">All in just 15 minutes!</h5>
                <div className="text-center">
                  <OptinButton />
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="landing-roadmap" color="purple" type="triangle" className="text-center text-white">
          <Container>
            <Row>
              <Col>
                <h3 className="section-only-heading">
                  Join this free Mini-Masterclass to get an easy roadmap to going plant-based!
                </h3>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL
          id="landing-host"
          color="light-gray"
          imgPosition="right"
          imgUrl="https://cdn.foodrevolution.org/global/team/frn-guest_expert-portrait-square-ocean_robbins.jpg"
          type="triangle"
          collapseHeader={
            <>
              <h5 className="text-400 m-0">Click Here to</h5>
              <h4 className="m-0">Learn More About Ocean Robbins</h4>
            </>
          }
          collapseHeadlines={
            <>
              <h2 className="section-heading mb-2">Your Host, Ocean Robbins</h2>
              <h4 className="m-0 text-400">
                A visionary leader and trusted guide for a healthier, more sustainable future.
              </h4>
            </>
          }
          collapseContent={
            <>
              <p>
                Ocean Robbins, co-founder and CEO of the million-member Food Revolution Network, is a TEDx speaker,
                best-selling author, husband, father, gardener, and dancer. Born in a log cabin and raised on home-grown
                food, his work is rooted in his family’s health-conscious legacy. From founding the nonprofit YES! at
                age 16 to leading a global food revolution movement, Ocean is on a mission to transform our food system,
                drive down the chronic disease epidemic, and mobilize people worldwide to be part of the solution for a
                healthier planet and population. His books include <i>31-Day Food Revolution</i> and{' '}
                <i>Powered By Plants.</i>
              </p>
            </>
          }
        />
        <SectionCL id="landing-frn" color="white" type="triangle" className="text-center">
          <Container>
            <Row>
              <Col>
                <img
                  className="mb-4"
                  src="https://cdn.foodrevolution.org/global/frn-logo-2024-trademarked-color.svg"
                  alt="Food Revolution Network logo"
                  width={300}
                  height={126}
                  style={{maxHeight: '200px'}}
                />
                <p className="balance-text">
                  Food Revolution Network educates and advocates for healthy, ethical, and sustainable food for all.
                </p>
                <p className="balance-text">
                  With a community of over a million and working alongside leading voices in food and health, we deliver
                  credible, research-based guidance to transform how the world eats.
                </p>
                <p className="balance-text">
                  As a certified B Corp, we commit to these values in every aspect of our work.
                </p>
                <img
                  src="https://cdn.foodrevolution.org/global/b-corp-logo-black.svg"
                  alt="B Corp Logo"
                  width={103}
                  height={150}
                  style={{maxHeight: '200px'}}
                />
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL
          id="landing-cta-final"
          color="light-gray"
          type="triangle"
          className="text-center"
          angle="none"
          order="last"
        >
          <Container>
            <Row>
              <Col>
                <div className="background-white box-shadow-black-50 p-4 p-lg-5 rounded">
                  <h3 className="section-heading text-purple">Your health is in your hands.</h3>
                  <p className="balance-text">
                    In this 15-minute Mini-Masterclass, you’ll discover why millions of people are choosing a
                    plant-powered lifestyle to get the most out of life — from boosting energy and improving heart
                    health to reducing chronic inflammation and achieving mental clarity.
                  </p>
                  <p className="balance-text">
                    Even if you’ve struggled with your health in the past, it’s never too late to start making choices
                    that could transform your well-being. What you learn has the potential to add many more healthy,
                    vibrant years to your life. Plus, it can feel really good to know that you’re part of the solution
                    on planet Earth.
                  </p>
                  <p className="balance-text mb-4">
                    Let Ocean show you what’s at stake and how to get started with simple, practical tips for choosing
                    the best foods for your energy, health, and happiness.
                  </p>
                  <OptinButton />
                  {variant != 'ad' && (
                    <>{defaultSchedule.phase === 'during' && <LandingTimer className="mt-4" dark />}</>
                  )}
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>
      </div>

      <div className="footer-content mt-auto">
        <Footer />
      </div>

      <OptinModal entry={pricePointEntry} bucket={bucket} variant={variant} btnText={<>Reserve Your Free Spot Now</>} />
      {!noExitIntent && <ModalExitLanding />}
    </div>
  );
}
